import React, {useContext, useEffect, useState} from 'react';
import {useLocation} from 'react-router';
import {useHistory} from 'react-router-dom';

import {useViewerUserInfoQuery} from '../generated/graphql';
import {isCreateTeamPath, isUserSettingsPath} from '../routes';
import {useViewer} from '../state/viewer/hooks';
import {useRampFlagAccountSelector} from '../util/rampFeatureFlags/rampFeatureFlags';
import {createNewTeamPage} from '../util/urls';
import {AccountSelectorContext} from './Search/SearchNav/AccountSelectorContextProvider';
import {AccountType} from './Search/SearchNav/types';
import {useAccountList} from './Search/SearchNav/useAccountList';

// Redirect users who belong to no team to CreateNewTeam page
const UserWithNoOrgsRedirect = () => {
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    const shouldRedirect =
      !isCreateTeamPath(location.pathname) &&
      !isUserSettingsPath(location.pathname);
    if (shouldRedirect) {
      history.push(createNewTeamPage());
    }
  }, [location.pathname, history]);
  return null;
};

// Redirect users who have no usable accounts to create a new team.
// This covers two scenarios:
// 1. Personal entity does not exist and user belongs to no orgs → empty account list.
// 2. Personal entity is hidden and it is the user's only account (orphaned user).
export const UserWithNoOrgEnforcement = () => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const enableAccountSelector = useRampFlagAccountSelector();
  const viewer = useViewer();
  const {data: viewerData, loading: userInfoLoading} = useViewerUserInfoQuery();
  const {accountList: accountListData, isLoading: accountListLoading} =
    useAccountList();
  const userInfo = viewerData?.viewer?.userInfo;
  const shouldHidePersonalEntity = userInfo?.isPersonalEntityHidden ?? false;

  const [viewerState, setViewerState] = useState(viewer);

  useEffect(() => {
    setViewerState(viewer);
  }, [viewer]);

  const numberOfViewerOrgs = viewerState?.organizations.length;

  if (
    !enableAccountSelector ||
    userInfoLoading ||
    accountListLoading ||
    numberOfViewerOrgs === undefined ||
    userInfo?.isOnboardingIncomplete === true
  ) {
    return null;
  }

  const hasNoUsableAccounts =
    accountListData.length === 0 && numberOfViewerOrgs === 0;

  const isSinglePersonalAccount =
    accountListData.length === 1 &&
    accountListData[0].name === viewerState?.username &&
    accountListData[0].accountType === AccountType.Personal;
  const hasOnlyHiddenPersonalEntity =
    shouldHidePersonalEntity &&
    isSinglePersonalAccount &&
    selectedAccount?.accountType === AccountType.Personal &&
    numberOfViewerOrgs === 0;

  const shouldEnforce = hasNoUsableAccounts || hasOnlyHiddenPersonalEntity;

  return shouldEnforce ? <UserWithNoOrgsRedirect /> : null;
};
